import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import {
  ConversionDescriptor,
  MarketingBinding,
  MarketingConsent,
  MarketingIntegration,
  MarketingReport,
  MarketingMeasurementSession,
} from "./marketing.models";
import { LocalStorageService } from "../local-storage.service";
import { SKIP_ERROR_INTERCEPTOR } from "../../interceptors/skip-interceptor.token";
import { map } from "rxjs";

@Injectable({ providedIn: "root" })
export class MarketingApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.API_BACKEND;
  private readonly storage = inject(LocalStorageService);
  private readonly httpContext = new HttpContext().set(
    SKIP_ERROR_INTERCEPTOR,
    true,
  );
  private headers() {
    let token: string | null = null;
    try {
      token = this.storage.getItem("auth-token");
    } catch {
      /* SSR/private browser */
    }
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();
  }
  private options() {
    return { headers: this.headers(), context: this.httpContext };
  }
  consent(body: unknown) {
    return this.http.post<MarketingConsent>(
      `${this.base}/marketing/v1/consents`,
      body,
      { context: this.httpContext },
    );
  }
  measurementSession(body: { previous_measurement_receipt?: string }) {
    return this.http.post<MarketingMeasurementSession>(
      `${this.base}/marketing/v1/measurement_sessions`,
      body,
      { context: this.httpContext },
    );
  }
  context(body: unknown) {
    return this.http.post<{
      context_token: string;
      event_id: number;
      expires_at: string;
    }>(`${this.base}/marketing/v1/contexts`, body, {
      context: this.httpContext,
    });
  }
  publicConfiguration(eventId: number, receipt?: string) {
    return this.http.get<{
      destinations: import("./marketing.models").MarketingDestination[];
      /** @deprecated Legacy response compatibility; never gates producer UI. */
      ui_enabled?: boolean;
    }>(`${this.base}/events/${eventId}/marketing/public_configuration`, {
      context: this.httpContext,
      params: receipt ? { consent_receipt: receipt } : {},
    });
  }
  descriptor(orderId: number, receipt: string) {
    return this.http.get<ConversionDescriptor>(
      `${this.base}/orders/${orderId}/marketing/conversion_descriptor`,
      { ...this.options(), params: { consent_receipt: receipt } },
    );
  }
  journey(body: unknown) {
    return this.http.post<{ recorded: boolean }>(
      `${this.base}/marketing/v1/journeys`,
      body,
      { context: this.httpContext },
    );
  }
  report(eventId: number, filters: Record<string, string>) {
    return this.http.get<MarketingReport>(
      `${this.base}/events/${eventId}/marketing/report`,
      { ...this.options(), params: filters },
    );
  }
  export(eventId: number, filters: Record<string, string>) {
    return this.http.get(`${this.base}/events/${eventId}/marketing/export`, {
      ...this.options(),
      params: new HttpParams({ fromObject: filters }),
      responseType: "blob",
    });
  }
  eventRegistrations(eventId: number) {
    return this.http.get<{ integrations: MarketingIntegration[] }>(
      `${this.base}/events/${eventId}/marketing/registrations`,
      this.options(),
    );
  }
  saveEventRegistration(
    eventId: number,
    integration: unknown,
    id?: number,
    key?: string,
  ) {
    const url = `${this.base}/events/${eventId}/marketing/registrations${id ? `/${id}` : ""}`;
    const request = id
      ? this.http.patch<{ integration: MarketingIntegration }>(
          url,
          { integration },
          this.options(),
        )
      : this.http.post<{ integration: MarketingIntegration }>(
          url,
          { integration },
          {
            ...this.options(),
            headers: this.headers().set(
              "Idempotency-Key",
              key || crypto.randomUUID(),
            ),
          },
        );
    return request.pipe(map((response) => response.integration));
  }
  saveEventBindings(eventId: number, bindings: MarketingBinding[]) {
    return this.http.put<{ bindings: MarketingBinding[] }>(
      `${this.base}/events/${eventId}/marketing/registration_bindings`,
      { bindings },
      this.options(),
    );
  }
  bindings(eventId: number) {
    return this.http.get<{ bindings: MarketingBinding[] }>(
      `${this.base}/events/${eventId}/marketing/integrations`,
      this.options(),
    );
  }
}
